import React from 'react';

import { permissions } from '@vlabs/shared/config';
import { useModal } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { fetchLists } from '@vlabs/pages/lists/store';

import type { ButtonWithPermissionsProps } from '@vlabs/monokit/shared/ui/button-with-permissions';
import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';

import type { CreateListProps } from './types';
import { ListForm } from '../../entities/lists';
import { useCreateList } from '@vlabs/api/luna-api/client';
import type { NewList } from '@vlabs/api/luna-api/model';
import { buildTestid } from '../../shared/lib/utils';

interface CreateListButtonProps extends
  CreateListProps,
  WithDataTestid,
  Omit<ButtonWithPermissionsProps, 'onClick' | 'permissions'> {}

const displayName = 'CreateListButton';
const testid = buildTestid(displayName);
function CreateListButton({
  onCreateList = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const createListModal = useModal();
  const dispatch = useDispatch();
  const createList = useCreateList();

  const onSubmit = async ({ user_data }: NewList) => {
    await createList.mutateAsync({ data: { user_data } }, {
      onSuccess: ({ list_id }) => {
        fetchLists(dispatch);
        createListModal.close();
        toast.success(t('lists:confirmation.create.success', { list: user_data }));
        onCreateList?.(list_id);
      },
    });
  };

  return (
    <React.Fragment>
      {createListModal.wrap(<ListForm
        context="create"
        onCancel={createListModal.close}
        onSubmit={onSubmit}
      />)}
      <ButtonWithPermissions
        data-testid={dataTestId ? testid(dataTestId)['data-testid'] : 'table.addButton'}
        onClick={createListModal.open}
        permissions={{ rules: [permissions.list.creation] }}
        {...props}
      >
        {t('lists:widgets.create list widget.button.create button.label')}
      </ButtonWithPermissions>
    </React.Fragment>
  );
}

CreateListButton.displayName = displayName;

export { CreateListButton };
